import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import logo from '../../assets/images/logo.png';
import maleIcon from '../../assets/images/male_icon.svg';
import femaleIcon from '../../assets/images/female_icon.svg';
import qrCode from '../../assets/images/qr.png';
import { Button } from 'antd';
import badge from '../../assets/images/badge.png';
import banner_image_url from '../../assets/images/bureau_banner.jpg';
import locationIcon from '../../assets/images/location_icon.png';
import telIcon from '../../assets/images/tel_icon.png';
import addressIcon from '../../assets/images/address_icon.png';
import decor_footer from '../../assets/images/decor_footer.png';

const API_URL = 'https://india-s3-dev-photo-video-uploads.s3.ap-south-1.amazonaws.com/app/bureau/bureau_data.json';

const BureauShare = () => {
    const [bureauData, setBureauData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchBureauData = async () => {
            try {
                setLoading(true);
                setError(null);
                
                const response = await fetch(API_URL);
                
                if (!response.ok) {
                    throw new Error(`Failed to fetch data: ${response.status} ${response.statusText}`);
                }
                
                // Check content type
                const contentType = response.headers.get('content-type');
                if (contentType && !contentType.includes('application/json') && !contentType.includes('text/json')) {
                    console.warn('Unexpected content type:', contentType);
                }
                
                // Get response as text first to handle potential JSON parsing issues
                let text = await response.text();
                
                // Check if response is HTML (error page) instead of JSON
                if (text.trim().startsWith('<!DOCTYPE') || text.trim().startsWith('<html')) {
                    throw new Error('Server returned HTML instead of JSON. This might be a CORS or server error page.');
                }
                
                // Remove BOM and other invisible characters at the start
                text = text.replace(/^\uFEFF/, '').trim();
                
                // Remove any leading/trailing whitespace and newlines
                text = text.trim();
                
                // Log the raw response for debugging (first 200 chars)
                console.log('Raw API response (first 200 chars):', text.substring(0, 200));
                
                // Try to parse the response
                // The API returns JavaScript object syntax (unquoted keys) instead of JSON
                let data;
                
                // Check if it's JavaScript object syntax (unquoted keys) or JSON
                const isJavaScriptSyntax = text.includes('bureau:') && !text.includes('"bureau"');
                
                if (isJavaScriptSyntax) {
                    // Parse as JavaScript object syntax using Function constructor (safe eval)
                    try {
                        console.log('Parsing as JavaScript object syntax...');
                        // Remove trailing commas first
                        const cleanedText = text
                            .replace(/,\s*}/g, '}')
                            .replace(/,\s*]/g, ']');
                        // eslint-disable-next-line no-new-func
                        data = new Function('return ' + cleanedText)();
                        console.log('Successfully parsed as JavaScript object');
                    } catch (jsError) {
                        console.error('Failed to parse as JavaScript object:', jsError);
                        throw new Error(`Failed to parse JavaScript object syntax: ${jsError.message}`);
                    }
                } else {
                    // Try to parse as JSON
                    try {
                        // Clean up common JSON issues
                        let cleanedText = text
                            .replace(/,\s*}/g, '}')  // Remove trailing commas before }
                            .replace(/,\s*]/g, ']')  // Remove trailing commas before ]
                            .replace(/,\s*,/g, ','); // Remove double commas
                        
                        data = JSON.parse(cleanedText);
                    } catch (parseError) {
                        console.error('JSON Parse Error:', parseError);
                        console.error('Response text (first 500 chars):', text.substring(0, 500));
                        throw new Error(`Invalid JSON response from server. ${parseError.message}`);
                    }
                }
                
                // Handle the API response structure
                // API structure: { bureau: { banner: {...}, profile_counts: {...}, featured_profiles: {...} } }
                console.log('Parsed data:', data);
                
                if (data && data.bureau) {
                    console.log('Bureau data structure:', {
                        hasBanner: !!data.bureau.banner,
                        hasProfileCounts: !!data.bureau.profile_counts,
                        hasFeaturedProfiles: !!data.bureau.featured_profiles
                    });
                    setBureauData(data);
                } else {
                    // If structure is different, try to handle it
                    console.warn('Unexpected data structure:', data);
                    // Check if data itself is the bureau object
                    if (data.banner || data.profile_counts) {
                        // It might be that the response is just the bureau object directly
                        setBureauData({ bureau: data });
                    } else {
                        throw new Error('Invalid data structure received from API');
                    }
                }
            } catch (err) {
                console.error('Error fetching bureau data:', err);
                setError(err.message || 'Failed to load bureau data');
            } finally {
                setLoading(false);
            }
        };

        fetchBureauData();
    }, []);

    // Default/fallback data structure matching API structure
    const defaultData = {
        bureau: {
            banner: {
                image: banner_image_url,
                title: "Elite matrimony collective",
                subtitle: "Bureau Information",
                contact_number: "",
                address: "",
                bureau_id: "",
                user_id: "",
                location: "",
                is_verified: false
            },
            profile_counts: {
                male: 0,
                female: 0,
                total: 0
            },
            featured_profiles: {
                male: {
                    total: 0,
                    profiles: []
                },
                female: {
                    total: 0,
                    profiles: []
                }
            }
        }
    };

    const data = bureauData || defaultData;
    const bureau = data.bureau || {};
    const banner = bureau.banner || {};
    const isVerified = banner.is_verified || false;

    // Loading state
    if (loading) {
        return (
            <div className='bureau_share_container'>
                <div className="public_header mb0">
                    <Link to="/login" className="main_logo">
                        <img src={logo} alt="logo" />
                    </Link>
                </div>
                <div style={{ padding: '50px', textAlign: 'center' }}>
                    <p>Loading bureau data...</p>
                </div>
            </div>
        );
    }

    // Error state
    if (error) {
        return (
            <div className='bureau_share_container'>
                <div className="public_header mb0">
                    <Link to="/login" className="main_logo">
                        <img src={logo} alt="logo" />
                    </Link>
                </div>
                <div style={{ padding: '50px', textAlign: 'center' }}>
                    <p style={{ color: 'red' }}>Error: {error}</p>
                    <p>Please try again later.</p>
                </div>
            </div>
        );
    }

    return (
        <div className='bureau_share_container'>
            <div className="public_header mb0">
                <Link to="/login" className="main_logo">
                    <img src={logo} alt="logo" />
                </Link>
            </div>

            {/* Banner Section */}
            <section className="bureau_banner">
                <figure className="banner_image">
                    <img src={banner.image || banner_image_url} alt="banner" />
                </figure>
                {
                    isVerified && (
                        <figure className="badge">
                            <img src={badge} alt="badge" />
                        </figure>
                    )
                }
               
                <div className='banner_wrap'>
                    <div className="banner_content">
                        <h2>{banner.subtitle || 'Bureau Information'}</h2>
                        <h1>{banner.title || 'Elite matrimony collective'}</h1>
                    </div>

                    <ul className="contact_details">
                        {banner.location && (
                            <li>
                                <span><img src={locationIcon} alt="" /></span>
                                <span>{banner.location}</span>
                            </li>
                        )}

                        {banner.contact_number && (
                            <li>
                                <span><img src={telIcon} alt="" /></span>
                                <span>{banner.contact_number}</span>
                            </li>
                        )}

                        {banner.address && (
                            <li>
                                <span><img src={addressIcon} alt="" /></span>
                                <span>{banner.address}</span>
                            </li>
                        )}
                    </ul>
                </div>
            </section>

            {/* Profile Counts */}
            <section className="profile_counts">
                <ul>
                    <li className="male">
                        <div>
                            <span><img src={maleIcon} alt="male icon" /></span>
                            <div>
                                <span>Male Profiles</span>
                                <span>{bureau.profile_counts?.male || 0}</span>
                            </div>
                        </div>
                    </li>

                    <li className="female">
                        <div>
                            <span><img src={femaleIcon} alt="female icon" /></span>
                            <div>
                                <span>Female Profiles</span>
                                <span>{bureau.profile_counts?.female || 0}</span>
                            </div>
                        </div>
                    </li>
                </ul>
            </section>

            {/* Featured Profiles */}
            <section className="featured_profiles">
                <div className="featured_container">

                    {/* Male */}
                    {bureau.featured_profiles?.male?.profiles && bureau.featured_profiles.male.profiles.length > 0 && (
                        <div>
                            <h2>Featured <span className='text_red'>Male</span> Profiles</h2>
                            <ul>
                                {bureau.featured_profiles.male.profiles.map((profile, index) => (
                                    <li key={profile.id || index}>
                                        <div className="img_holder_sm">
                                            <img src={profile.image || ''} alt={profile.id || `male-profile-${index}`} />
                                        </div>
                                        <div className="text-center">
                                            <span className="text_red">{profile.id || 'N/A'}</span>
                                            <span>{profile.age || 'N/A'} Yrs | {profile.height || 'N/A'} | {profile.caste || 'N/A'}</span>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {/* Female */}
                    {bureau.featured_profiles?.female?.profiles && bureau.featured_profiles.female.profiles.length > 0 && (
                        <div>
                            <h2>Featured <span className='text_red'>Female</span> Profiles</h2>
                            <ul>
                                {bureau.featured_profiles.female.profiles.map((profile, index) => (
                                    <li key={profile.id || index}>
                                        <div className="img_holder_sm">
                                            <img src={profile.image || ''} alt={profile.id || `female-profile-${index}`} />
                                        </div>
                                        <div className="text-center">
                                            <span className="text_red">{profile.id || 'N/A'}</span>
                                            <span>{profile.age || 'N/A'} Yrs | {profile.height || 'N/A'} | {profile.caste || 'N/A'}</span>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            </section>

            {/* Footer */}
            <footer>
                <div className="footer_container">
                    <div className="qr_code">
                        <img src={qrCode} alt="QR Code" />
                    </div>
                    <div className='logo_wrap'>
                        <Button>Download Now</Button>
                        <figure>
                            <img src={decor_footer} alt="" />
                        </figure>
                    </div>
                    <div>
                        <Link to="/login" className="main_logo">
                            <img src={logo} alt="logo" />
                        </Link>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default BureauShare;
